import { alpha, useTheme } from "@mui/material/styles";
import type { SxProps, Theme } from "@mui/material/styles";
import { botMessageColors, botMessageColorsDark } from "../../../../colors/colors";

export function getVisualizationReplySurfaces(theme: Theme): {
  replyBg: string;
  replyBar: string;
} {
  const replyBg =
    theme.palette.mode === "dark"
      ? alpha(theme.palette.common.white, 0.08)
      : alpha(theme.palette.common.black, 0.06);
  const replyBar =
    theme.palette.mode === "dark"
      ? alpha(theme.palette.common.white, 0.45)
      : theme.palette.grey[400];
  return { replyBg, replyBar };
}

export function useVisualizationMessageStyles({
  showTimestamp,
  showActions,
}: {
  showTimestamp: boolean;
  showActions: boolean;
}) {
  const theme = useTheme();
  const { replyBg, replyBar } = getVisualizationReplySurfaces(theme);
  const timestampColor = (
    theme.palette.mode === "dark" ? botMessageColorsDark : botMessageColors
  ).system.timeColor;

  const onGradientText =
    theme.palette.mode === "dark"
      ? theme.palette.common.white
      : theme.palette.text.primary;

  const styles: Record<string, SxProps<Theme>> = {
    root: { width: "100%", maxWidth: "100%" },
    gradientOuter: {
      position: "relative",
      borderRadius: 2,
      paddingTop: 1,
      overflow: "hidden",
      background: "linear-gradient(90deg, #28C9DC 0%, #8957CF 100%)",
    },
    innerCard: {
      m: 1,
      borderRadius: 1,
      bgcolor: "linear-gradient(90deg, #28C9DC 0%, #8957CF 100%)",
      overflow: "hidden",
    },
    title: {
      px: 1.5,
      pt: 1.25,
      pb: 0.75,
      fontSize: "0.95rem",
      fontWeight: 600,
      color: onGradientText,
    },
    text: {
      px: 1.5,
      pb: 1,
      fontSize: "0.9rem",
      color: onGradientText,
      opacity: 0.95,
      "& .markdown p": { margin: 0 },
    },
    replyWrap: { px: 1, pb: 1 },
    replyButton: {
      position: "relative",
      background: replyBg,
      alignItems: "stretch",
      borderTopRightRadius: "10px",
      borderBottomRightRadius: "10px",
      borderTopLeftRadius: "5px",
      borderBottomLeftRadius: "5px",
      cursor: "pointer",
      pl: "12px",
    },
    replyAccentBar: {
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: "6px",
      background: replyBar,
      borderTopLeftRadius: "10px",
      borderBottomLeftRadius: "10px",
    },
    replyTypography: {
      padding: "10px",
      flex: 1,
      fontSize: "0.875rem",
      color: onGradientText,
      "& .custom-formatted-text": { color: "inherit" },
    },
    contentWrap: { px: 1, pb: 1 },
    image: {
      boxShadow: "0px 5px 15px rgba(0, 0, 0, 0.35)",
    },
    galleryWrapper: {
      width: "100%",
      maxWidth: "100%",
    },
    galleryImageBox: {
      position: "relative",
      width: "100%",
      borderRadius: 1,
      overflow: "hidden",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      touchAction: "pan-y",
    },
    galleryImg: {
      width: "100%",
      height: "auto",
      display: "block",
      borderRadius: 1,
      boxShadow: "0px 5px 15px rgba(0, 0, 0, 0.35)",
    },
    galleryNav: {
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%)",
      zIndex: 2,
      background: "rgba(0, 0, 0, 0.45)",
      color: "#fff",
      width: 36,
      height: 36,
      "&:hover": { background: "rgba(0, 0, 0, 0.6)" },
      "&.prev": { left: 8 },
      "&.next": { right: 8 },
    },
    slideBadge: {
      position: "absolute",
      bottom: 10,
      right: 10,
      px: 1,
      py: 0.25,
      borderRadius: 10,
      fontSize: "0.75rem",
      fontWeight: 600,
      color: "#fff",
      background: alpha("#8957CF", 0.92),
      lineHeight: 1.4,
    },
    actionsRow: {
      display: "flex",
      justifyContent: "flex-end",
      gap: 0.5,
      mt: 0.5,
      mb: showTimestamp ? 1 : 0,
    },
    iconButton: { color: timestampColor },
    iconBusy: { opacity: 0.5 },
    timestamp: {
      display: "block",
      textAlign: "right",
      px: 1.25,
      pb: 1,
      pt: showActions ? 0.5 : 0,
      fontSize: "12px",
      whiteSpace: "nowrap",
      color: timestampColor,
      fontFamily: "Red Hat Display",
      fontWeight: 500,
    },
    timestampSpacer: { margin: "0 5px", display: "inline" },
  };

  return styles;
}
